import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { useOwnerFetch, OwnerUnlockPrompt } from '../lib/ownerFetch';

export default function OwnerCourseRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const { ownerFetch, unlockProps } = useOwnerFetch();

  useEffect(() => {
    Promise.all([
      api.get('/owner/course-requests?status=pending'),
      api.get('/owner/course-requests?status=resolved'),
      api.get('/owner/course-requests?status=rejected'),
    ]).then(([pending, resolved, rejected]) => {
      setRequests([
        ...(pending.data?.course_requests || []),
        ...(resolved.data?.course_requests || []),
        ...(rejected.data?.course_requests || []),
      ]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openRequests = requests.filter(r => r.status === 'pending');
  const closedRequests = requests.filter(r => r.status !== 'pending');

  const handleResolve = (cr) => {
    const encodedName = encodeURIComponent(cr.course_name || '');
    navigate(`/admin/add-course?from_request=${cr.id}&prefill_name=${encodedName}`);
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActing(rejectTarget.id);
    const target = rejectTarget;
    const reason = rejectReason.trim() || null;
    const result = await ownerFetch(() => api.post(`/owner/course-requests/${target.id}/reject`, { reason }));
    if (result.ok) {
      toast.success('Request rejected — email sent to organiser');
      setRequests(prev => prev.map(r => r.id === target.id ? { ...r, status: 'rejected' } : r));
      setRejectTarget(null);
      setRejectReason('');
    } else if (result.error) {
      toast.error(result.error);
    }
    setActing(null);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="course-requests-page">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white p-1"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
        <h1 className="text-lg text-white font-bold">Course Requests</h1>
      </div>
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        <OwnerUnlockPrompt {...unlockProps} />

        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}</div>
        ) : openRequests.length === 0 && closedRequests.length === 0 ? (
          <Card className="p-6 text-center bg-white border-[#E1E6E4] rounded-xl">
            <p className="text-[#455561] text-sm">No course requests</p>
          </Card>
        ) : (
          <>
            {openRequests.length === 0 ? (
              <Card className="p-5 text-center bg-white border-[#E1E6E4] rounded-xl">
                <p className="text-sm text-[#455561]">No open course requests</p>
              </Card>
            ) : openRequests.map(cr => (
              <Card key={cr.id} className="p-4 bg-white border-[#E1E6E4] rounded-xl" data-testid={`cr-row-${cr.id}`}>
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#1D242B] truncate">{cr.course_name}</p>
                      <p className="text-xs text-[#455561]">{cr.organiser_name || cr.organiser_email}</p>
                      {cr.organiser_name && <p className="text-[10px] text-[#B8C7C0]">{cr.organiser_email}</p>}
                    </div>
                    <Badge className="bg-[#E3F2FD] text-[#1565C0] text-[9px] flex-shrink-0">Pending</Badge>
                  </div>
                  <p className="text-[10px] text-[#B8C7C0]">
                    Event: {cr.event_name || '—'} · {cr.created_at ? new Date(cr.created_at).toLocaleDateString() : ''}
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={() => handleResolve(cr)} disabled={acting === cr.id}
                      className="h-8 px-3 text-xs bg-[#1B4332] text-white rounded-lg" data-testid={`cr-resolve-${cr.id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" weight="bold" />Resolve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setRejectTarget(cr); setRejectReason(''); }}
                      disabled={acting === cr.id}
                      className="h-8 px-3 text-xs border-[#E1E6E4] text-[#D32F2F] rounded-lg" data-testid={`cr-reject-${cr.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" weight="bold" />Reject
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {closedRequests.length > 0 && (
              <button onClick={() => setShowClosed(!showClosed)} className="flex items-center gap-1 text-xs text-[#455561] font-semibold" data-testid="toggle-closed">
                {showClosed ? <CaretDown className="w-3.5 h-3.5" /> : <CaretRight className="w-3.5 h-3.5" />}
                {closedRequests.length} closed
              </button>
            )}
            {showClosed && (
              <div className="mt-2 space-y-2">
                {closedRequests.map(cr => (
                  <Card key={cr.id} className="p-3 bg-[#F4F6F5] border-[#E1E6E4] rounded-xl opacity-60">
                    <p className="text-xs font-semibold text-[#1D242B]">{cr.course_name}</p>
                    <p className="text-[10px] text-[#455561]">{cr.organiser_name || cr.organiser_email} · {cr.status}</p>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader><DialogTitle className="text-sm font-bold text-[#1D242B]">Reject request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#455561]">Course: {rejectTarget?.course_name}</p>
            <Input placeholder="Optional reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="text-sm" data-testid="reject-reason-input" />
            <Button onClick={handleReject} disabled={acting === rejectTarget?.id}
              className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white rounded-lg" data-testid="reject-confirm-btn">
              {acting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
